import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtCVE, fmtDate, PAYMENT_METHODS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, CashRegister, CircleNotch, Plus, Trash, ShoppingCart, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

function MethodSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(PAYMENT_METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function MovementDialog({ type, onClose, onDone }) {
  const [f, setF] = useState({ amount: "", description: "", method: "numerario", date: today() });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ amount: "", description: "", method: "numerario", date: today() }); }, [type]);
  const save = async () => {
    if (!f.description.trim() || !(Number(f.amount) > 0)) return toast.error("Indique descrição e valor");
    setBusy(true);
    try {
      await api.post("/cash/movements", { ...f, type, amount: Number(f.amount) });
      toast.success("Movimento registado");
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="movement-dialog">
        <DialogHeader><DialogTitle>{type === "in" ? "Entrada de caixa" : "Saída de caixa"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>Descrição</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="movement-description" /></div>
          <div className="space-y-1.5"><Label>Valor (CVE)</Label><Input type="number" min="0" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="movement-amount" /></div>
          <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="movement-date" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Método</Label><MethodSelect value={f.method} onChange={(v) => setF({ ...f, method: v })} testid="movement-method" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="save-movement-btn">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuickSale({ open, onClose, onDone }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [method, setMethod] = useState("numerario");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setCart({});
    api.get("/products", { params: { active: true, limit: 500 } }).then((r) => setProducts(r.data.items)).catch(() => {});
  }, [open]);
  const add = (id, d) => setCart((c) => { const n = Math.max(0, (c[id] || 0) + d); const x = { ...c, [id]: n }; if (!n) delete x[id]; return x; });
  const total = Object.entries(cart).reduce((s, [id, qty]) => {
    const p = products.find((x) => x.id === id);
    return s + (p ? p.price * qty * (1 + (p.vat_rate || 0) / 100) : 0);
  }, 0);
  const submit = async () => {
    const items = Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));
    if (!items.length) return toast.error("Adicione produtos");
    setBusy(true);
    try {
      const { data } = await api.post("/cash/quick-sale", { items, method });
      toast.success(`Venda ${data.number} registada (recibo interno)`);
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const list = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="quick-sale-dialog">
        <DialogHeader><DialogTitle>Venda rápida</DialogTitle></DialogHeader>
        <Input placeholder="Pesquisar produto..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="quick-sale-search" />
        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {list.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Sem produtos. Crie produtos em Produtos.</p>}
          {list.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2" data-testid={`qs-product-${p.id}`}>
              <div className="min-w-0"><div className="truncate text-sm font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{fmtCVE(p.price)}{p.track_stock ? ` · stock ${p.stock_qty}` : ""}</div></div>
              <div className="flex items-center gap-1.5">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => add(p.id, -1)} data-testid={`qs-minus-${p.id}`}><Minus size={14} /></Button>
                <span className="w-6 text-center text-sm font-bold" data-testid={`qs-qty-${p.id}`}>{cart[p.id] || 0}</span>
                <Button size="icon" className="h-8 w-8" onClick={() => add(p.id, 1)} data-testid={`qs-plus-${p.id}`}><Plus size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-1.5"><Label>Método de pagamento</Label><MethodSelect value={method} onChange={setMethod} testid="quick-sale-method" /></div>
        <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
          <span className="text-sm">Total (c/ IVA)</span><span className="font-display text-xl font-extrabold" data-testid="quick-sale-total">{fmtCVE(total)}</span>
        </div>
        <p className="text-[11px] text-muted-foreground">Gera um recibo interno — documento sem valor fiscal.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="quick-sale-submit">{busy ? <CircleNotch className="animate-spin" /> : "Registar venda"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Cash() {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [mv, setMv] = useState(null);
  const [sale, setSale] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cash/movements", { params: { date_from: from || undefined, date_to: to || undefined } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm("Eliminar este movimento?")) return;
    try { await api.delete(`/cash/movements/${m.id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="cash-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Tesouraria</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Caixa</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => setMv("out")} data-testid="cash-out-btn"><ArrowUp size={16} className="mr-1.5 text-red-600" /> Saída</Button>
          <Button variant="outline" className="rounded-full" onClick={() => setMv("in")} data-testid="cash-in-btn"><ArrowDown size={16} className="mr-1.5 text-emerald-600" /> Entrada</Button>
          <Button className="rounded-full" onClick={() => setSale(true)} data-testid="quick-sale-btn"><ShoppingCart size={16} className="mr-1.5" /> Venda rápida</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">De</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cash-from" /></div>
        <div className="space-y-1"><Label className="text-xs">Até</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cash-to" /></div>
        <Button variant="ghost" size="sm" onClick={() => { setFrom(""); setTo(""); }} data-testid="cash-all">Todo o período</Button>
      </div>

      {!data ? <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs text-muted-foreground">Entradas</div><div className="font-display text-xl font-extrabold text-emerald-700" data-testid="cash-period-in">{fmtCVE(data.period.in)}</div></div>
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs text-muted-foreground">Saídas</div><div className="font-display text-xl font-extrabold text-red-600" data-testid="cash-period-out">{fmtCVE(data.period.out)}</div></div>
            <div className="rounded-xl border border-border bg-card p-4"><div className="text-xs text-muted-foreground">Saldo do período</div><div className="font-display text-xl font-extrabold" data-testid="cash-period-balance">{fmtCVE(data.period.balance)}</div></div>
            <div className="rounded-xl border border-primary/40 bg-accent p-4"><div className="text-xs text-accent-foreground">Saldo total em caixa</div><div className="font-display text-xl font-extrabold text-accent-foreground" data-testid="cash-overall-balance">{fmtCVE(data.overall.balance)}</div></div>
          </div>
          <div className="rounded-xl border border-border bg-card">
            {data.items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-center"><CashRegister size={40} className="text-muted-foreground" /><p className="text-muted-foreground">Sem movimentos neste período.</p></div>
            ) : (
              <ul className="divide-y divide-border">
                {data.items.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-3" data-testid={`movement-${m.id}`}>
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${m.type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                      {m.type === "in" ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{m.description}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(m.date)} · {PAYMENT_METHODS[m.method] || m.method} · {{ manual: "Manual", payment: "Venda/recebimento", expense: "Despesa" }[m.source] || m.source}</div>
                    </div>
                    <div className={`whitespace-nowrap text-sm font-bold ${m.type === "in" ? "text-emerald-700" : "text-red-600"}`}>{m.type === "in" ? "+" : "−"}{fmtCVE(m.amount)}</div>
                    {m.source === "manual" ? (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(m)} data-testid={`delete-movement-${m.id}`}><Trash size={15} /></Button>
                    ) : <span className="w-8" />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
      <MovementDialog type={mv} onClose={() => setMv(null)} onDone={() => { setMv(null); load(); }} />
      <QuickSale open={sale} onClose={() => setSale(false)} onDone={() => { setSale(false); load(); }} />
    </div>
  );
}
